import React, { useMemo, useState, useEffect } from 'react';
import { useCompare } from '../context/CompareContext';
import { Link, useNavigate } from 'react-router-dom';
import { priceInfo } from '../utils/priceUtils';
import { useCurrency } from '../context/CurrencyContext';
import cartService from '../services/cartService';
import wishlistService from '../services/wishlistService';
import toast from 'react-hot-toast';
import './ComparePage.css';

export default function ComparePage() {
    const { comparedProducts, removeFromCompare } = useCompare();
    const { formatPrice } = useCurrency();
    const navigate = useNavigate();
    const [wishlistItems, setWishlistItems] = useState([]);
    const [cartItems, setCartItems] = useState([]);

    useEffect(() => {
        const fetchData = async () => {
            const [wList, cItems] = await Promise.all([
                wishlistService.getWishlist(),
                                                      cartService.getCart()
            ]);
            setWishlistItems(wList);
            setCartItems(cItems);
        };
        fetchData();

        const handleWishlistUpdate = (e) => setWishlistItems(e.detail?.items || []);
        const handleCartUpdate = (e) => setCartItems(e.detail?.items || []);

        window.addEventListener('wishlistUpdated', handleWishlistUpdate);
        window.addEventListener('cartUpdated', handleCartUpdate);
        return () => {
            window.removeEventListener('wishlistUpdated', handleWishlistUpdate);
            window.removeEventListener('cartUpdated', handleCartUpdate);
        };
    }, []);

    const isInWishlist = (id) => wishlistItems.some(i => String(i.id) === String(id));
    const isInCart = (id) => cartItems.some(i => String(i.id) === String(id));

    const handleWishlistToggle = async (product) => {
        try {
            await wishlistService.toggleItem(product);
            toast.success(isInWishlist(product.id) ? 'U hoq nga lista e dëshirave' : 'U shtua në listën e dëshirave');
        } catch (err) {
            toast.error('Dështoi përditësimi i listës së dëshirave');
        }
    };

    const handleCartToggle = async (product) => {
        const info = priceInfo(product);
        if (product.stock != null && Number(product.stock) <= 0) {
            toast.error('Jashtë stokut');
            return;
        }
        try {
            if (isInCart(product.id)) {
                await cartService.removeItem(product.id);
                toast.success('U hoq nga shporta');
            } else {
                await cartService.addItem({
                    id: product.id,
                    name: product.name,
                    price: info.display,
                    image: product.image,
                    sku: product.sku
                }, 1);
                toast.success('U shtua në shportë');
            }
        } catch (err) {
            toast.error('Dështoi përditësimi i shportës');
        }
    };

    const handleBuyNow = async (product) => {
        const info = priceInfo(product);
        if (product.stock != null && Number(product.stock) <= 0) {
            toast.error('Jashtë stokut');
            return;
        }
        try {
            await cartService.addItem({
                id: product.id,
                name: product.name,
                price: info.display,
                image: product.image,
                sku: product.sku
            }, 1);
            navigate('/cart');
        } catch (err) {
            toast.error('Dështoi shtimi në shportë');
        }
    };

    // Extract all unique specification keys across all compared products
    const uniqueSpecKeys = useMemo(() => {
        const keys = new Set();
        comparedProducts.forEach(product => {
            let specsObj = product.specs;
            if (typeof specsObj === 'string') {
                try { specsObj = JSON.parse(specsObj); } catch (_) { }
            }
            if (specsObj && typeof specsObj === 'object') {
                Object.keys(specsObj).forEach(key => keys.add(key));
            }
        });
        return Array.from(keys).sort();
    }, [comparedProducts]);

    if (comparedProducts.length === 0) {
        return (
            <div className="empty-compare">
            <h2>Krahaso Produktet</h2>
            <p>Nuk ka produkte të zgjedhura për krahasim.</p>
            <Link to="/products" className="browse-btn">
            Shfletoni Produktet
            </Link>
            </div>
        );
    }

    const getSpecValue = (product, key) => {
        let specsObj = product.specs;
        if (typeof specsObj === 'string') {
            try { specsObj = JSON.parse(specsObj); } catch (_) { }
        }
        if (specsObj && typeof specsObj === 'object' && specsObj[key] !== undefined) {
            return specsObj[key];
        }
        return null;
    };

    return (
        <div className="compare-container">
        <div className="compare-header-row">
        <h2 className="compare-title">Krahaso Produktet</h2>
        {comparedProducts.length < 5 && (
            <Link to="/products" className="add-product-top-btn">
            <span>+</span> Shto Produkt
            </Link>
        )}
        </div>
        <div className="compare-table-wrapper">
        <div style={{ overflowX: 'auto' }}>
        <table className="compare-table">
        <thead>
        <tr>
        <th className="feature-label">Produkti</th>
        {comparedProducts.map((p) => (
            <th key={p.id} className="product-col">
            <button
            onClick={() => removeFromCompare(p.id)}
            className="remove-btn"
            title="Hiq"
            >
            ×
            </button>
            <div className="compare-product-image-box">
            <img
            src={(p.images && p.images.length) ? p.images[0] : p.image}
            alt={p.name}
            className="compare-product-image"
            />
            </div>
            <Link to={`/products/${p.id}`} className="compare-product-name">
            {p.name}
            </Link>
            </th>
        ))}
        {comparedProducts.length < 5 && (
            <th className="add-more-col">
            <Link to="/products" className="add-more-link">
            <div className="add-more-icon">+</div>
            <span>Shto Produkt</span>
            </Link>
            </th>
        )}
        </tr>
        </thead>
        <tbody>
        <tr>
        <td className="feature-label">Çmimi</td>
        {comparedProducts.map((p) => {
            const pInfo = priceInfo(p);
            return (
                <td key={p.id} className="product-col">
                <div className={`compare-price-val ${pInfo.isOffer || pInfo.isSale ? 'on-sale' : ''}`}>
                {formatPrice(pInfo.display)}
                </div>
                </td>
            );
        })}
        {comparedProducts.length < 5 && <td className="add-more-col"></td>}
        </tr>
        <tr>
        <td className="feature-label">Marka</td>
        {comparedProducts.map((p) => (
            <td key={p.id} className="product-col spec-value">
            {p.brand || '—'}
            </td>
        ))}
        {comparedProducts.length < 5 && <td className="add-more-col"></td>}
        </tr>
        <tr>
        <td className="feature-label">Kategoria</td>
        {comparedProducts.map((p) => (
            <td key={p.id} className="product-col spec-value">
            {p.category || '—'}
            </td>
        ))}
        {comparedProducts.length < 5 && <td className="add-more-col"></td>}
        </tr>

        {/* Dynamic Specifications Rows */}
        {uniqueSpecKeys.map(specKey => (
            <tr key={specKey}>
            <td className="feature-label">{specKey}</td>
            {comparedProducts.map((p) => {
                const specValue = getSpecValue(p, specKey);
                return (
                    <td key={p.id} className="product-col">
                    {specValue !== null ? (
                        <div style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}>
                        <span className="spec-check">✓</span>
                        <span className="spec-value">{String(specValue)}</span>
                        </div>
                    ) : (
                        <span className="empty-spec">—</span>
                    )}
                    </td>
                );
            })}
            {comparedProducts.length < 5 && <td className="add-more-col"></td>}
            </tr>
        ))}

        <tr>
        <td className="feature-label">Veprimet</td>
        {comparedProducts.map((p) => {
            const pInfo = priceInfo(p);
            return (
                <td key={p.id} className="compare-actions-cell">
                <div className="compare-action-buttons">
                <div className="compare-bottom-prices">
                {pInfo.discounted ? (
                    <>
                    <span className="compare-new-price">{formatPrice(pInfo.display)}</span>
                    <span className="compare-price-separator">|</span>
                    <span className="compare-old-price">{formatPrice(pInfo.original)}</span>
                    </>
                ) : (
                    <span className="compare-new-price">{formatPrice(pInfo.display)}</span>
                )}
                </div>
                <div className="compare-main-actions">
                <button
                className="compare-buy-btn"
                onClick={() => handleBuyNow(p)}
                >
                BLI TANI
                </button>
                <button
                className={`compare-icon-btn wishlist ${isInWishlist(p.id) ? 'active' : ''}`}
                onClick={() => handleWishlistToggle(p)}
                title="Shto në dëshira"
                >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M19 14c1.49-1.46 3-3.21 3-5.5A5.5 5.5 0 0 0 16.5 3c-1.76 0-3 .5-4.5 2-1.5-1.5-2.74-2-4.5-2A5.5 5.5 0 0 0 2 8.5c0 2.3 1.5 4.05 3 5.5l7 7Z" />
                </svg>
                </button>
                <button
                className={`compare-icon-btn cart ${isInCart(p.id) ? 'active' : ''}`}
                onClick={() => handleCartToggle(p)}
                title="Shto në shportë"
                >
                <svg viewBox="0 0 24 24" width="20" height="20" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="8" cy="21" r="1" />
                <circle cx="19" cy="21" r="1" />
                <path d="M2.05 2.05h1.5l2.66 12.42a2 2 0 0 0 2 1.58h9.78a2 2 0 0 0 1.95-1.57l1.65-7.43H5.12" />
                </svg>
                </button>
                </div>
                </div>
                </td>
            );
        })}
        {comparedProducts.length < 5 && <td className="add-more-col"></td>}
        </tr>
        </tbody>
        </table>
        </div>
        </div>
        </div>
    );
}
